import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useEventListener } from 'usehooks-ts';

import { PaymentContext } from 'contexts/PaymentContext';
import { PopupNames } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';
import { FromIframe, ToIframe } from 'types/enums/IframeEvents';

import { usePartnerId } from 'hooks/usePartnerId';
import { usePopup } from 'hooks/usePopup';
import { getUserCountry } from 'store/auth/selectors';
import { isComericaBankEnabledSelector } from 'store/common/selectors';

export const useLifetimeOfferPopup = () => {
  const { pathname } = useLocation();
  const { sendMessageToIframe } = useContext(PaymentContext);

  const isComericaBankSplitEnabled = useSelector(isComericaBankEnabledSelector);
  const country = useSelector(getUserCountry);

  const IsUserFromUS = useMemo(() => country === 'US', [country]);

  const { handleOpenForce, handleClose, handleAction, checkIsOpen } = usePopup(
    PopupNames.LifetimeOffer
  );

  const { isOrganicPartnerId } = usePartnerId();

  const isLifetimeOfferEnabled = useMemo(
    () =>
      isComericaBankSplitEnabled &&
      IsUserFromUS &&
      MirrorService.isFirstSiteGroup &&
      !isOrganicPartnerId,
    [IsUserFromUS, isComericaBankSplitEnabled, isOrganicPartnerId]
  );

  const handlePostMessage = useCallback(
    (msg: MessageEvent) => {
      if (!msg?.data?.event) return;

      if (msg.data.event === FromIframe.IsComericaBin) {
        if (isLifetimeOfferEnabled) {
          handleOpenForce({});
        } else {
          sendMessageToIframe({
            event: ToIframe.FormSubmit,
          });
        }
      }
    },
    [handleOpenForce, isLifetimeOfferEnabled, sendMessageToIframe]
  );

  useEventListener('message', handlePostMessage);

  useEffect(() => {
    handleAction.current = (action: { type: string }) => {
      if (action.type === 'close') handleClose();
    };
  }, [handleAction, handleClose]);

  useEffect(() => {
    if (!pathname.includes('/payment') && checkIsOpen()) {
      handleClose();
    }
  }, [checkIsOpen, handleClose, pathname]);
};
